import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { User } from 'lucide-react';
import { useVoiceEngine } from '@/hooks/useVoiceEngine';
import { getScenarioByCode } from '@/data/insurance-scenarios';
import { type InsuranceProduct } from '@/data/insurance-products';
import {
  getProductRecommendations,
  type ProductComparison,
} from '@/services/product-recommendations';
import { ClientProfilePanel } from './components/ClientProfilePanel';
import { ProductRecommendationsPanel } from './components/ProductRecommendationsPanel';
import { ConversationPanel } from './components/ConversationPanel';
import {
  InlineRealtimeFeedback,
  type FeedbackTip,
} from './components/InlineRealtimeFeedback';
import { InsuranceFeedbackAnalysis } from './components/InsuranceFeedbackAnalysis';
import { LoadingOverlay } from '@/components/ui/loading';
import { ErrorMessage } from '@/components/loading-error';
import api from '@/utils/ky';

type LoadingState =
  | 'initial'
  | 'loading'
  | 'ready'
  | 'active'
  | 'error'
  | 'connecting'
  | 'ending';

// Voice configuration array
const VOICE_OPTIONS = [
  {
    label: 'kai',
    voice_id: 'Ori1rnHIeeysIxrsFZ2X',
  },
  {
    label: 'anna-kim',
    voice_id: 'uyVNoMrnUku1dZyVEXwD',
  },
  {
    label: 'leeho',
    voice_id: 'dfGdOKe4Py3B2cmOtW1k',
  },
  {
    label: 'karaey',
    voice_id: 'z2P4oCxSHhXan3ew4COv',
  },
  {
    label: 'jaedon-ahn',
    voice_id: 'ZcZcEsYxXAIc3nNSev1H',
  },
  {
    label: 'choi',
    voice_id: 'ZNSVYmudV9pOqphY0x8C',
  },
  {
    label: 'yura',
    voice_id: 'F7wT70V3u09d2rY9pNa6',
  },
  {
    label: 'jeong-ah',
    voice_id: 'UvkXHIJzOBYWOI51BDKp',
  },
  {
    label: 'haeryeokyun',
    voice_id: 'a52RveZOORPA9buQulXm',
  },
  {
    label: 'taehyung',
    voice_id: 'm3gJBS8OofDJfycyA2Ip',
  },
  {
    label: 'jisoo',
    voice_id: 'iWLjl1zCuqXRkW6494ve',
  },
];

export function InsuranceRoleplay() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scenarioCode = searchParams.get('scenario');
  const language = searchParams.get('lang') || 'ko';
  const voiceLabel = searchParams.get('voice') || 'kai';

  // Use v6 for AIA-2 by default, but allow manual override with ?version=X
  const defaultVersion = scenarioCode === 'aia-2' ? '6' : '1';
  const apiVersion = parseInt(
    searchParams.get('version') || defaultVersion,
    10,
  );

  // Get voice ID from voice label
  const selectedVoice = VOICE_OPTIONS.find((v) => v.label === voiceLabel);
  const voiceId = selectedVoice?.voice_id || VOICE_OPTIONS[0].voice_id; // Default to kai

  const { t, i18n } = useTranslation();

  // State
  const [loadingState, setLoadingState] = useState<LoadingState>('initial');
  const [error, setError] = useState<string | null>(null);
  const [isPracticeStarted, setIsPracticeStarted] = useState(false);
  const [transcript, setTranscript] = useState<
    Array<{
      source: 'ai' | 'user';
      message: string;
      timestamp: Date;
    }>
  >([]);
  const [recommendedProducts, setRecommendedProducts] = useState<
    InsuranceProduct[]
  >([]);
  const [productComparisons, setProductComparisons] = useState<
    ProductComparison[]
  >([]); // NEW: For comparison mode
  const [responseMode, setResponseMode] = useState<
    'no_action' | 'recommend' | 'compare'
  >('no_action'); // NEW: Track mode
  const [isClientSpeaking, setIsClientSpeaking] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(true);
  const [showHintToast, setShowHintToast] = useState(false);
  const [feedbackTips, setFeedbackTips] = useState<FeedbackTip[]>([]); // Real-time feedback tips
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackData, setFeedbackData] = useState<any>(null);
  const [mobileModalOpen, setMobileModalOpen] = useState<
    'none' | 'client' | 'products'
  >('none');

  // Voice engine
  const voiceEngine = useVoiceEngine(language, 'elevenlabs');
  const { status, isSpeaking: isAISpeaking } = voiceEngine;

  // Refs
  const startTimeRef = useRef<number>(0);
  const voiceEngineRef = useRef(voiceEngine);
  const pendingRequestRef = useRef<boolean>(false);

  // Load scenario
  const scenario = useMemo(() => {
    if (!scenarioCode) return null;
    return getScenarioByCode(scenarioCode);
  }, [scenarioCode]);

  // Update voice engine ref
  useEffect(() => {
    voiceEngineRef.current = voiceEngine;
  }, [voiceEngine]);

  // Auto-collapse left panel on mobile and close modals on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        // lg breakpoint
        setIsLeftPanelCollapsed(true);
        setMobileModalOpen('none');
      }
    };

    // Set initial state
    handleResize();

    // Listen to resize events
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile modals on ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileModalOpen !== 'none') {
        setMobileModalOpen('none');
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [mobileModalOpen]);

  // Prevent body scroll when mobile modals are open
  useEffect(() => {
    if (mobileModalOpen !== 'none') {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileModalOpen]);

  // Set language
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  // Redirect if no scenario
  useEffect(() => {
    if (!scenarioCode || !scenario) {
      console.error('No scenario code provided or scenario not found');
      navigate('/');
      return;
    }
    setLoadingState('ready');
  }, [scenarioCode, scenario, navigate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const currentEngine = voiceEngineRef.current;
      if (
        currentEngine &&
        (currentEngine.status === 'connected' ||
          currentEngine.status === 'connecting')
      ) {
        console.log('🧹 Cleaning up voice engine on unmount');
        currentEngine.stop().catch((err) => {
          console.error('Error stopping voice engine during cleanup:', err);
        });
      }
    };
  }, []);

  // Show hint toast when practice starts (only once)
  useEffect(() => {
    if (isPracticeStarted && isLeftPanelCollapsed) {
      // Show toast after a short delay (1 second after practice starts)
      const showTimer = setTimeout(() => {
        setShowHintToast(true);
      }, 1000);

      // Auto-hide after 4 seconds
      const hideTimer = setTimeout(() => {
        setShowHintToast(false);
      }, 5000);

      return () => {
        clearTimeout(showTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [isPracticeStarted, isLeftPanelCollapsed]);

  // Real-time product recommendation using LlamaIndex API
  // Prevents duplicate requests by waiting for current request to complete
  const updateProductRecommendations = async (
    latestTranscript: typeof transcript,
  ) => {
    console.log('---scenario:', scenario, 'latestTranscript', latestTranscript);
    if (!scenario || latestTranscript.length === 0) {
      setRecommendedProducts([]);
      return;
    }

    console.log('------pendingRequestRef.current', pendingRequestRef.current);

    // If a request is already in progress, skip this call
    // Let the current request finish naturally (no cancellation)
    if (pendingRequestRef.current) {
      console.log(
        '⏭️ Request already in progress, skipping new request (will wait for current to finish)',
      );
      return;
    }

    // Mark request as in progress
    pendingRequestRef.current = true;

    try {
      console.log('🚀 Starting product recommendation request');

      // Call the backend API for recommendations
      const response = await getProductRecommendations({
        scenarioCode: scenarioCode!,
        conversationHistory: latestTranscript.map((msg) => ({
          source: msg.source,
          message: msg.message,
          timestamp: msg.timestamp.toISOString(),
        })),
        clientPersona: {
          age: scenario.clientPersona.age,
          occupation: scenario.clientPersona.occupation,
          concerns: scenario.clientPersona.concerns,
        },
        version: apiVersion, // Pass version from URL parameter
      });
      console.log('-------response:', response);

      // Handle response based on mode
      setResponseMode(response.mode);

      if (response.mode === 'no_action') {
        // Mode A: No action needed - keep existing data
        console.log('ℹ️ No action mode - keeping existing data');
      } else if (response.mode === 'recommend') {
        // Mode B: Product recommendations
        if (response.shouldUpdate && response.products.length > 0) {
          console.log('✅ Received product recommendations:', {
            count: response.products.length,
            concerns: response.analysisMetadata.detectedConcerns,
            context: response.analysisMetadata.conversationContext,
          });

          // Update with top 5 products from API
          setRecommendedProducts(response.products.slice(0, 5));
          setProductComparisons([]); // Clear comparisons when in recommend mode
        } else {
          console.log(
            'ℹ️ No products recommended (shouldUpdate=false or empty products)',
          );
          setRecommendedProducts([]);
        }
      } else if (response.mode === 'compare') {
        // Mode C: Product comparisons
        if (response.comparisons && response.comparisons.length > 0) {
          console.log('✅ Received product comparisons:', {
            count: response.comparisons.length,
            competitors: response.analysisMetadata.detectedCompetitors,
            context: response.analysisMetadata.conversationContext,
          });

          setProductComparisons(response.comparisons);
          setRecommendedProducts([]); // Clear products when in compare mode
        } else {
          console.log('ℹ️ No comparisons generated');
          setProductComparisons([]);
        }
      }
    } catch (error) {
      console.error('❌ Failed to fetch product recommendations:', error);

      // IMPORTANT: On error, KEEP previous recommendations (as specified)
      // The backend returns HTTP 500 with { "error": "message" } on errors
      // So we don't clear the recommendations - user keeps seeing last valid set
      console.log('⚠️ Keeping previous recommendations due to API error');

      // You could optionally show a subtle toast notification here
      // to let the user know the update failed (but don't disrupt UX)
    } finally {
      // Clear pending state
      pendingRequestRef.current = false;
      console.log('✅ Request completed, ready for next update');
    }
  };

  // Handle start practice
  const handleStartPractice = async () => {
    if (loadingState === 'connecting' || !scenario) {
      console.log('⚠️ Already connecting or no scenario, ignoring click');
      return;
    }

    try {
      setLoadingState('connecting');
      console.log('🚀 Starting insurance practice session:', {
        scenarioCode,
        language,
        agentId: scenario.agentId,
        voiceId: scenario.clientPersona.voiceId,
      });

      // Request microphone access
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('🎤 Microphone access granted');
      } catch (micError) {
        console.error('🎤 Microphone access error:', micError);
        throw new Error('MICROPHONE_DENIED');
      }

      // Generate scenario-specific voice prompt
      const getVoicePrompt = () => {
        if (scenarioCode === 'aia-2') {
          // AIA-2: Comparative Positioning Scenario
          return `## WHO YOU ARE
You are ${scenario.clientPersona.name} (${scenario.clientPersona.nameKo}), a ${scenario.clientPersona.age}-year-old ${scenario.clientPersona.occupation}.
You have a wife and two children (ages 10 and 13).

## YOUR SITUATION (SECOND MEETING - COMPARISON STAGE)
You've already done your homework. You met with several insurance companies and reviewed Universal Life products from AIA, Samsung Life (삼성생명), Allianz (알리안츠), and MetLife (메트라이프). They all look very similar on paper. You're here for a second meeting with the AIA agent to understand WHY you should choose AIA instead of the others.

## YOUR CURRENT COVERAGE
You already have basic term life insurance from another company:
- Death benefit: "이 억원" (200 million won) - MUST say with space
- Medical: Basic coverage

CRITICAL: If asked about current coverage:
✅ Say: "지금은 다른 회사 정기보험으로 사망 보장 이 억원 정도 있어요"
❌ NEVER say: "몇억원" (forbidden)

## WHEN ASKED ABOUT FAMILY OR AGE:
- Family: "아내랑 아이 둘 있어요. 애들은 열 살, 열세 살이고요."
  * DO NOT mention your own age when describing family
- Your age (ONLY if directly asked "나이가 어떻게 되세요?"): "마흔다섯 살입니다"
  * Use "살" (NOT "세")

## YOUR MINDSET
- Skeptical but open-minded
- Want concrete reasons, not sales talk
- Looking for DATA and FACTS (rates, costs, flexibility)
- Concerned about: family protection, inheritance tax, but also want to make sure you're getting the BEST product`;
        } else {
          // AIA-1: Needs Discovery Scenario (existing)
          return `## WHO YOU ARE
You are ${scenario.clientPersona.name} (${scenario.clientPersona.nameKo}), a ${scenario.clientPersona.age}-year-old ${scenario.clientPersona.occupation}.
You have a wife and two children (ages 10 and 13).

## YOUR SITUATION
You came to meet with an insurance agent because you have concerns about protecting your family and managing inheritance taxes. You ALREADY have basic life insurance from another company with a death benefit of "이 억원" (200 million won). Your business is stable, and you're mostly worried about making sure your family's lifestyle doesn't change if something happens to you — and maybe about future inheritance taxes.

## YOUR CURRENT INSURANCE COVERAGE (BE SPECIFIC WHEN ASKED)
When asked about your current insurance:
- Death benefit: "이 억원" (200 million won) - MUST say with space between 이 and 억
- Coverage type: Basic term life insurance from another company
- Medical: Basic hospitalization and surgery, small amounts

CRITICAL: When discussing coverage amounts:
✅ Say: "사망 보장은 이 억원이요" or "사망 보장금이 이 억원 정도 됩니다"
✅ Say: "의료비는 입원비랑 수술비가 기본으로 조금 들어가 있어요"
❌ NEVER say: "몇억원" (forbidden - too vague)
❌ NEVER say: "수억원" (forbidden - too vague)

## WHEN ASKED ABOUT FAMILY OR AGE:
- Family: "아내랑 아이 둘 있어요. 애들은 열 살, 열세 살이고요."
  * DO NOT mention your own age when describing family
  * Keep it natural and short
- Your age (ONLY if directly asked "나이가 어떻게 되세요?" or "몇 살이세요?"): "마흔다섯 살입니다"
  * Use "살" (NOT "세")
  * Do NOT randomly add your age at the end of other answers

## CRITICAL: REALISTIC MONTHLY LIVING EXPENSES
When the agent asks about monthly living expenses needed for your family:
- Your ESSENTIAL living expenses (생활비) are around **KRW 5,000,000/month** (오백만원)
- This covers: housing, food, utilities, daily necessities, children's basic needs
- SEPARATE from this, you have:
  * Mortgage/loan payments: around KRW 3,000,000/month
  * Children's education/tuition: around KRW 2,000,000/month
  * Other expenses (car, insurance, etc.): varies

IMPORTANT: When asked "얼마 정도 필요하세요?" or "한 달에 생활비가 얼마나 드세요?":
- First mention: "음... 기본 생활비는 한 달에 **오백만원** 정도요" (essential living: ~5M won)
- If they ask about total expenses: "대출이랑 학원비까지 합치면 천만원 정도 나가긴 하는데, 순수 생활비는 오백만원 정도입니다"
- DO NOT say 25-30 million won/month - that's unrealistic
- Keep essential living expenses realistic for a middle-class Korean family`;
        }
      };

      // Korean-specific number formatting rules for TTS pronunciation
      const getKoreanNumberRules = () => {
        if (language !== 'ko') return '';

        return `[🚨 CRITICAL - 숫자 출력 규칙 / NUMBER OUTPUT RULE - READ THIS FIRST 🚨]

═══════════════════════════════════════════════════════════════
⚠️ ABSOLUTE RULE #1: NEVER USE VAGUE AMOUNTS - ALWAYS BE SPECIFIC
═══════════════════════════════════════════════════════════════

❌❌❌ FORBIDDEN WORDS - NEVER SAY THESE: ❌❌❌
- "몇억원" (myeok-eok-won) - BANNED
- "수억원" (su-eok-won) - BANNED
- "몇천만원" - BANNED
- "대충", "얼추", "어느 정도" - BANNED
- ANY vague amount descriptions - BANNED

✅✅✅ REQUIRED: ALWAYS USE SPECIFIC NUMBERS ✅✅✅
When discussing insurance coverage or money, ALWAYS say exact amounts:
- "일억원" (100 million won)
- "이 억원" (200 million won - NOTE THE SPACE)
- "삼억원" (300 million won)
- "오억원" (500 million won)
- "일억오천만원" (150 million won)

If you need to express a range, use TWO specific numbers:
✅ "일억원에서 이 억원 사이" (between 100-200 million)
✅ "대략 일억오천만원" (approximately 150 million)
❌ NEVER "몇억원 정도" (vague - forbidden)

═══════════════════════════════════════════════════════════════
⚠️ ABSOLUTE RULE #2: PRONUNCIATION RULES FOR "억" (hundred million)
═══════════════════════════════════════════════════════════════

GENERAL RULE - NO SPACES (for most amounts):
✅ "일억원" (100 million won) - NO space
✅ "삼억원" (300 million won) - NO space
✅ "오억원" (500 million won) - NO space
✅ "십억원" (1 billion won) - NO space

SPECIAL EXCEPTION - "2억 원" REQUIRES SPACE:
✅ "이 억원" (200 million won) - MUST have space between 이 and 억
❌ "이억원" - WRONG - causes mispronunciation (sounds like "yiukkkwon")

The space is CRITICAL for "이 억원" to pronounce correctly as "yi-uk-won"

═══════════════════════════════════════════════════════════════
⚠️ RULE #3: USE SINO-KOREAN NUMBERS FOR MONEY AND TIME
═══════════════════════════════════════════════════════════════

ALWAYS use SINO-KOREAN (일, 이, 삼, 사, 오, 육, 칠, 팔, 구, 십):
- Money: "삼억원" (300 million won)
- Years: "십년" (10 years), "오년" (5 years)
- Percentages: "사점구 퍼센트" (4.9%)

NEVER use:
❌ Native Korean (하나, 둘, 셋, 한, 열) for money/time
❌ Arabic numerals (1, 2, 3, 100억)
❌ Mixed format like "2억 원" (use "이 억원")

═══════════════════════════════════════════════════════════════
📋 CRITICAL EXAMPLES FOR YOUR CURRENT INSURANCE COVERAGE
═══════════════════════════════════════════════════════════════

When asked about your current death benefit coverage, you MUST say:
✅ "사망 보장은 이 억원이요" (Death benefit is 200 million won)
✅ "사망 보장금이 이 억원 정도 됩니다" (Death benefit is about 200 million won)

❌ NEVER say "사망 보장은 몇억원이요" - FORBIDDEN
❌ NEVER say "사망 보장은 수억원이요" - FORBIDDEN

More Examples:
- "100 million won" → "일억원" (NOT "몇억원", "한 억", "1억")
- "200 million won" → "이 억원" (MUST have space - NOT "이억원", "몇억원")
- "300 million won" → "삼억원" (NOT "몇억원")
- "500 million won" → "오억원" (NOT "몇억원")
- "150 million won" → "일억오천만원" (NOT "몇억원")
- "1 billion won" → "십억원" (NOT "몇억원", "십억 원")
- "3 billion won" → "삼십억원" (NOT "몇십억원")

Range examples:
- "between 150-200 million" → "일억오천만원에서 이 억원 사이"
- "approximately 200 million" → "대략 이 억원"

Other numbers:
- "45 years old" → "마흔다섯 살" (use "살" NOT "세")
- "10 years" → "십년" (NOT "열년", "십 년")
- "$1,500,000" → "백오십만 달러"

═══════════════════════════════════════════════════════════════

`;
      };

      // Prepare voice configuration with natural conversation instructions
      const voiceConfig = {
        agentId: scenario.agentId,
        language,
        voiceId: scenario.clientPersona.voiceId,
        prompt: `${getKoreanNumberRules()}${getVoicePrompt()}

## CRITICAL: You are a CUSTOMER, NOT a service agent or consultant
YOU ARE THE CLIENT. THE OTHER PERSON IS THE AGENT/SALESPERSON.
- NEVER ask "What can I help you with?"
- NEVER ask "What kind of consultation do you want?"
- NEVER ask "Can I ask what concerns you have?"
- NEVER offer help or services
- NEVER explain insurance concepts or products to the agent
- NEVER give advice or recommendations to the agent
- YOU are seeking help, NOT providing it
- YOU answer questions, the AGENT asks questions
- If you find yourself explaining something to the agent, STOP - that's the agent's job, not yours

## LANGUAGE
IMPORTANT: You must speak ONLY in Korean. All your responses must be in Korean.

## CONVERSATION FLOW
${
  scenarioCode === 'aia-2'
    ? `1. First greeting: Say "${scenario.clientPersona.initialMessageKo}"
2. Second response (if agent acknowledges): Mention you've compared products like "네, 삼성, 알리안츠, 메트라이프까지 봤는데요. 솔직히 다 비슷해 보이더라고요. AIA만의 확실한 장점이 뭔지 궁금해서 왔습니다."
3. When agent explains differences: Ask SPECIFIC questions about:
   - "공시이율이 다른 회사랑 얼마나 차이나요?" (crediting rate difference)
   - "보험료가 나중에 올라가진 않나요?" (premium stability)
   - "중간에 돈이 필요하면 빼낼 수 있어요?" (withdrawal flexibility)
4. Challenge gently: "그런데 삼성도 비슷한 말을 하던데요?" (but Samsung said similar things)
5. After that: WAIT for agent to provide DATA. React to facts positively, but remain cautiously interested.
6. If asked about current coverage: "지금은 다른 회사 정기보험으로 사망 보장 이 억원 정도 있어요" (NEVER say "몇억원")`
    : `1. First greeting: Say "${scenario.clientPersona.initialMessageKo}"
2. Second response (if agent greets back): Briefly mention your situation like "이미 다른 회사에서 기본 생명보험은 가입했는데요. 사업은 안정적이고, 만약 저한테 무슨 일이 생기면 가족 생활이 바뀌지 않았으면 해서요. 그리고 나중에 상속세 부담도 좀 걱정되고요."
3. If asked about current coverage: Say "사망 보장은 이 억원이요. 의료비는 입원비랑 수술비가 기본으로 조금 들어가 있고요." (NEVER say "몇억원")
4. After that: WAIT for agent questions. ONLY answer what they ask. Do NOT volunteer information.`
}

## SPEAKING STYLE
- Keep responses SHORT: 1-2 sentences
- Answer ONLY what's asked - no volunteering info
- DO NOT add random information at the end of sentences
- If asked about family, talk about family only (don't mention your own age)
- If asked about your age, then mention age: "마흔다섯 살입니다"
- Use natural customer language: "음...", "글쎄요..."
- Speak naturally, not like reading a script
- Show slight uncertainty (you're not an expert)

Examples of natural responses:
Q: "가족은 어떻게 되세요?" (How's your family?)
✅ Good: "아내랑 아이 둘 있어요. 애들은 열 살, 열세 살이고요."
❌ Bad: "아내랑 아이 둘 있어요. 애들은 열 살, 열세 살이고요. 마흔다섯 살입니다." (awkward - don't mention your own age)

Q: "나이가 어떻게 되세요?" (How old are you?)
✅ Good: "마흔다섯 살입니다."
❌ Bad: "마흔다섯 세입니다." (grammatically wrong - use "살" not "세")

## YOUR BACKGROUND (share ONLY when directly asked)

### Personal Information:
- Your age: ${scenario.clientPersona.age} years old (마흔다섯 살)
  * ONLY mention your age if directly asked "나이가 어떻게 되세요?" or "몇 살이세요?"
  * Say: "마흔다섯 살입니다" (NOT "마흔다섯 세입니다")
  * DO NOT randomly mention your age when talking about other topics

- Occupation: ${scenario.clientPersona.occupation} (${
          scenario.clientPersona.occupationKo
        })

- Family: ${scenario.clientPersona.family.maritalStatus}, ${
          scenario.clientPersona.family.children
        } children
  * Children's ages: ${scenario.clientPersona.family.childrenAges.join(
    ', ',
  )} years old (열 살, 열세 살)
  * Wife: Around your age
  * When asked about family: "아내랑 아이 둘 있어요. 애들은 열 살, 열세 살이고요."
  * DO NOT mention your own age when describing family unless specifically asked

### Financial Information:
- Current coverage:
  * Term life insurance from another company
  * Death benefit: 이 억원 (200 million won) - MUST say "이 억원" with space
  * Medical coverage: Basic hospitalization and surgery coverage, small amounts

- Business: Mid-sized logistics company, ${
          scenario.clientPersona.financial.businessTypeKo
        }

- Income: ${scenario.clientPersona.financial.annualIncomeKo}

### Concerns & Personality:
- Main concerns: Family lifestyle protection, inheritance tax burden
- Personality: ${scenario.clientPersona.personality}

CRITICAL RULES - When to mention information:
✅ Age: ONLY if asked "나이가 어떻게 되세요?" → "마흔다섯 살입니다"
✅ Family: If asked "가족은 어떻게 되세요?" → "아내랑 아이 둘 있어요. 애들은 열 살, 열세 살이고요"
✅ Current insurance: If asked → "사망 보장은 이 억원이요"
❌ DO NOT volunteer your age when not asked
❌ DO NOT mention your age at the end of unrelated sentences
❌ NEVER say "몇억원" or any vague amount

## ABSOLUTE RULES - NEVER DO THESE
❌ NEVER ask "어떤 상담을 원하세요?" (What consultation do you want?)
❌ NEVER ask "도움이 필요하세요?" (Do you need help?)
❌ NEVER ask "무엇을 도와드릴까요?" (How can I help you?)
❌ NEVER say "도와드리겠습니다" (I'll help you)
❌ NEVER recommend products
❌ NEVER explain insurance concepts
❌ NEVER take expert/consultant role
❌ NEVER switch roles - YOU are ALWAYS the customer, THEY are ALWAYS the agent
❌ NEVER educate the agent about insurance products or financial planning
❌ NEVER use professional insurance terminology as if you're an expert

## REMEMBER - YOUR ROLE IS FIXED
You are ${scenario.clientPersona.name}, a CUSTOMER seeking advice, NOT giving advice.
The OTHER person is the INSURANCE AGENT/SALESPERSON.
Let the AGENT ask YOU questions.
You answer their questions naturally and gradually.
If you catch yourself explaining insurance concepts, STOP immediately - that's role reversal.

Speak ONLY in Korean.`,
        firstMessage: scenario.clientPersona.initialMessageKo,
      };
      console.log('🔌 Initiating ElevenLabs connection');

      const conversationId = await voiceEngine.start(voiceConfig, {
        onConnect: () => {
          console.log('✅ Successfully connected to ElevenLabs');
          startTimeRef.current = Date.now();
          setLoadingState('active');
          setIsPracticeStarted(true);
        },
        onError: (error) => {
          console.error('❌ ElevenLabs connection error:', error);
          setError(error?.message || error || 'Connection Error');
          setLoadingState('error');
        },
        onMessage: ({ source, message }) => {
          console.log('💬 Message received:', { source, message });

          setTranscript((prev) => {
            const newTranscript = [
              ...prev,
              {
                source,
                message,
                timestamp: new Date(),
              },
            ];

            // Update product recommendations when CLIENT (AI) speaks
            // This ensures recommendations are based on new information provided by the client
            if (source === 'ai') {
              const currentAiCount =
                prev.filter((m) => m.source === 'ai').length + 1;

              // Update starting from second AI message (after initial greeting exchange)
              if (currentAiCount >= 2) {
                console.log(
                  '🔄 Updating product recommendations (client message #' +
                    currentAiCount +
                    ')',
                );

                // Show loading state
                setIsLoadingProducts(true);

                // Call API with a minimum display time for loading state (1500-2500ms)
                const minDisplayTime = 1500 + Math.random() * 1000;
                const startTime = Date.now();

                updateProductRecommendations(newTranscript).finally(() => {
                  // Ensure loading state shows for minimum time
                  const elapsed = Date.now() - startTime;
                  const remainingTime = Math.max(0, minDisplayTime - elapsed);

                  setTimeout(() => {
                    setIsLoadingProducts(false);
                  }, remainingTime);
                });
              }
            }

            return newTranscript;
          });
        },
        onAgentSpeaking: (speaking) => {
          setIsClientSpeaking(speaking);
        },
      });

      console.log('🎯 Conversation started successfully:', conversationId);
    } catch (error) {
      console.error('💥 Error starting practice:', error);
      setError(
        error?.message === 'MICROPHONE_DENIED'
          ? '마이크 권한이 필요합니다. 브라우저 설정에서 마이크 권한을 허용해주세요.'
          : '연습 세션을 시작할 수 없습니다. 다시 시도해주세요.',
      );
      setLoadingState('error');
    }
  };

  // Handle end practice
  const handleEndPractice = async () => {
    try {
      setLoadingState('ending');
      console.log('🛑 Ending practice session...');

      // Clear pending state (let any in-flight request complete naturally)
      pendingRequestRef.current = false;

      // Stop voice engine
      if (voiceEngine) {
        await voiceEngine.stop();
      }

      // Calculate session duration
      const duration = (Date.now() - startTimeRef.current) / 1000;
      console.log('📊 Session duration:', duration, 'seconds');

      // Format transcript for API
      const formattedTranscript = transcript
        .map(
          (msg) =>
            `${msg.source === 'ai' ? 'Client' : 'Salesperson'}: ${msg.message}`,
        )
        .join('\n');

      // Generate feedback using API
      try {
        console.log('🔄 Requesting post-session feedback...');

        // Note: api already has prefixUrl configured, so just use relative path
        const feedback = await api
          .post('api/roleplay/feedback/session-analysis', {
            json: {
              scenarioCode,
              transcript: formattedTranscript,
              duration,
              language,
            },
            timeout: 90000, // 90 second timeout (same as Prudential)
          })
          .json();

        console.log('✅ Feedback received:', feedback);
        setFeedbackData(feedback);
        setShowFeedback(true);
      } catch (error) {
        console.error('❌ Failed to generate feedback:', error);
        // Show error but still allow user to see transcript
        setError(
          '피드백 생성에 실패했습니다. 세션은 정상적으로 종료되었습니다.',
        );
      }

      setIsPracticeStarted(false);
      setLoadingState('ready');
    } catch (error) {
      console.error('Error ending practice:', error);
      setLoadingState('error');
      setError('세션 종료 중 오류가 발생했습니다.');
    }
  };

  // Timer state for session duration
  const [sessionDuration, setSessionDuration] = useState(0);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPracticeStarted && loadingState === 'active') {
      interval = setInterval(() => {
        setSessionDuration((prev) => prev + 1);
      }, 1000);
    } else {
      setSessionDuration(0);
    }
    return () => clearInterval(interval);
  }, [isPracticeStarted, loadingState]);

  // Format duration mm:ss
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`;
  };

  if (!scenario) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-2xl shadow-lg border border-gray-100 max-w-md mx-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            시나리오를 찾을 수 없습니다
          </h1>
          <p className="text-gray-600">잘못된 시나리오 코드입니다.</p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#F8FAFC]">
      {/* Header - Modern Glassmorphism */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-200/80 px-3 md:px-6 py-2 md:py-3 shadow-sm transition-all duration-300">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="md:w-5 md:h-5"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-sm md:text-lg font-bold text-gray-900 tracking-tight">
                {t('insurance.title')}
              </h1>
              <p className="text-[10px] md:text-xs text-gray-500 font-medium">
                {language === 'ko'
                  ? '보험 세일즈 롤플레이'
                  : 'Insurance Sales Roleplay'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            {isPracticeStarted && (
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full border border-gray-200">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-sm font-mono font-medium text-gray-700">
                  {formatDuration(sessionDuration)}
                </span>
              </div>
            )}

            {isPracticeStarted && (
              <button
                onClick={handleEndPractice}
                className="group flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-white border border-red-200 text-red-600 text-xs md:text-sm rounded-lg md:rounded-xl hover:bg-red-50 hover:border-red-300 transition-all duration-200 font-semibold shadow-sm hover:shadow"
              >
                <span className="hidden sm:inline">
                  {t('insurance.endSession')}
                </span>
                <span className="sm:hidden"> {t('insurance.endSession')}</span>
                <svg
                  className="w-3.5 h-3.5 md:w-4 md:h-4 group-hover:translate-x-0.5 transition-transform"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content - 3 Panel Layout */}
      <div className="flex-1 overflow-hidden relative p-2 sm:p-4 md:p-6 pb-24 lg:pb-6">
        {/* Hint Toast - Elegant disappearing hint */}
        <AnimatePresence>
          {showHintToast && (
            <motion.div
              initial={{ opacity: 0, x: -20, y: 20 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{
                duration: 0.4,
                type: 'spring',
                stiffness: 400,
                damping: 30,
              }}
              className="absolute top-4 left-4 md:top-8 md:left-8 z-50 bg-gray-900/95 backdrop-blur text-white pl-3 pr-8 md:pl-4 md:pr-10 py-2.5 md:py-3.5 rounded-xl md:rounded-2xl shadow-2xl border border-white/10 flex items-center gap-2 md:gap-3 max-w-[calc(100vw-2rem)] sm:max-w-sm"
            >
              <div className="bg-blue-500/20 text-blue-400 rounded-full p-1.5 md:p-2">
                <User className="w-3.5 h-3.5 md:w-4 md:h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs md:text-sm font-bold mb-0.5 text-white truncate">
                  {language === 'ko' ? '고객 정보 패널' : 'Client Info Panel'}
                </div>
                <div className="text-[10px] md:text-xs text-gray-300 leading-relaxed">
                  {language === 'ko'
                    ? '왼쪽 화살표를 클릭하여 고객 정보를 확인하세요'
                    : 'Click the arrow to view client details'}
                </div>
              </div>
              <button
                onClick={() => setShowHintToast(false)}
                className="absolute top-1.5 right-1.5 md:top-2 md:right-2 p-0.5 md:p-1 text-white/40 hover:text-white transition-colors rounded-full hover:bg-white/10"
                aria-label="Dismiss"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="md:w-3.5 md:h-3.5"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile Floating Action Buttons */}
        <div className="lg:hidden fixed bottom-6 left-0 right-0 z-30 flex justify-center gap-3 px-4 pointer-events-none">
          <button
            onClick={() => setMobileModalOpen('client')}
            className="pointer-events-auto flex items-center gap-2 px-4 py-3 bg-white border-2 border-blue-500 text-blue-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 font-semibold"
          >
            <User className="w-4 h-4" />
            <span className="text-sm">
              {language === 'ko' ? '고객 정보' : 'Client'}
            </span>
          </button>
          <button
            onClick={() => setMobileModalOpen('products')}
            className="pointer-events-auto flex items-center gap-2 px-4 py-3 bg-white border-2 border-purple-500 text-purple-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 font-semibold"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <span className="text-sm">
              {language === 'ko' ? '추천 상품' : 'Products'}
            </span>
          </button>
        </div>

        <div className="max-w-[1800px] mx-auto h-full flex gap-2 sm:gap-4 md:gap-6">
          {/* Left Panel - Client Profile (Desktop: Collapsible Panel, Mobile: Modal) */}
          <div className="hidden lg:block flex-shrink-0 h-full transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1.0)]">
            <ClientProfilePanel
              persona={scenario.clientPersona}
              language={language as 'en' | 'ko'}
              isCollapsed={isLeftPanelCollapsed}
              onToggle={() => setIsLeftPanelCollapsed(!isLeftPanelCollapsed)}
            />
          </div>

          {/* Mobile Client Profile Modal */}
          <AnimatePresence>
            {mobileModalOpen === 'client' && (
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setMobileModalOpen('none')}
                  className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                />
                {/* Modal */}
                <motion.div
                  initial={{ x: -100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -100, opacity: 0 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className="lg:hidden fixed left-0 top-0 h-screen w-[90vw] max-w-md z-50 flex flex-col"
                >
                  <div className="flex-1 flex flex-col bg-white rounded-r-2xl shadow-2xl overflow-hidden">
                    <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                      <h2 className="font-bold text-gray-900">
                        {language === 'ko' ? '고객 정보' : 'Client Info'}
                      </h2>
                      <button
                        onClick={() => setMobileModalOpen('none')}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4">
                      <ClientProfilePanel
                        persona={scenario.clientPersona}
                        language={language as 'en' | 'ko'}
                        isCollapsed={false}
                        onToggle={() => {}}
                        showToggle={false}
                        fullWidth={true}
                      />
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* Center Panel - Conversation */}
          <div className="flex-1 flex flex-col min-w-0 h-full shadow-sm rounded-2xl overflow-hidden bg-white border border-gray-200/60">
            {/* Real-time Feedback Analyzer - runs in background */}
            {isPracticeStarted && scenarioCode && (
              <InlineRealtimeFeedback
                transcript={transcript}
                scenarioCode={scenarioCode}
                language={language as 'en' | 'ko'}
                setFeedbackTips={setFeedbackTips}
              />
            )}

            <ConversationPanel
              transcript={transcript}
              isAISpeaking={isClientSpeaking}
              persona={scenario.clientPersona}
              isPracticeStarted={isPracticeStarted}
              onStartPractice={handleStartPractice}
              isConnecting={loadingState === 'connecting'}
              language={language as 'en' | 'ko'}
              feedbackTips={feedbackTips}
            />
          </div>

          {/* Right Panel - Product Recommendations / Comparisons (Desktop: Panel, Mobile: Modal) */}
          <div className="hidden lg:block w-80 flex-shrink-0 h-full">
            <ProductRecommendationsPanel
              products={recommendedProducts}
              comparisons={productComparisons}
              responseMode={responseMode}
              isPracticeStarted={isPracticeStarted}
              isUpdating={isLoadingProducts}
              language={language as 'en' | 'ko'}
            />
          </div>

          {/* Mobile Product Recommendations Modal */}
          <AnimatePresence>
            {mobileModalOpen === 'products' && (
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setMobileModalOpen('none')}
                  className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                />
                {/* Modal */}
                <motion.div
                  initial={{ x: 100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: 100, opacity: 0 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className="lg:hidden fixed right-0 top-0 h-screen w-[90vw] max-w-md z-50 flex flex-col"
                >
                  <div className="flex-1 flex flex-col bg-white rounded-l-2xl shadow-2xl overflow-hidden">
                    <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                      <h2 className="font-bold text-gray-900">
                        {language === 'ko' ? '추천 상품' : 'Recommendations'}
                      </h2>
                      <button
                        onClick={() => setMobileModalOpen('none')}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4">
                      <ProductRecommendationsPanel
                        products={recommendedProducts}
                        comparisons={productComparisons}
                        responseMode={responseMode}
                        isPracticeStarted={isPracticeStarted}
                        isUpdating={isLoadingProducts}
                        language={language as 'en' | 'ko'}
                      />
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Loading States */}
      <AnimatePresence>
        {loadingState !== 'ready' && loadingState !== 'active' && (
          <LoadingOverlay state={loadingState} />
        )}
      </AnimatePresence>

      {/* Error Message */}
      {error && (
        <ErrorMessage
          message={error}
          onRetry={() => {
            setError(null);
            setLoadingState('ready');
          }}
        />
      )}

      {/* Post-Session Feedback Modal */}
      <AnimatePresence>
        {showFeedback && feedbackData && scenario && (
          <InsuranceFeedbackAnalysis
            feedbackData={feedbackData}
            language={language as 'en' | 'ko'}
            onClose={() => {
              setShowFeedback(false);
              // Clear session data
              setTranscript([]);
              setRecommendedProducts([]);
              setFeedbackTips([]);
              setFeedbackData(null);
            }}
            clientName={
              language === 'ko'
                ? scenario.clientPersona.nameKo
                : scenario.clientPersona.name
            }
            duration={(Date.now() - startTimeRef.current) / 1000}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
